/**
 * Get The Look — per-item size selection, add to bag, and notify when unavailable.
 */
(function () {
  function parseVariants(row) {
    const script = row.querySelector('.blog-look-product__variants-json');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  function findVariantForSize(variants, sizeOptionIndex, sizeValue) {
    if (sizeOptionIndex < 0 || !sizeValue) return null;
    const normalizedSize = String(sizeValue).trim();
    const matches = variants.filter((variant) => {
      const optionValue = variant[`option${sizeOptionIndex + 1}`];
      return String(optionValue).trim() === normalizedSize;
    });
    if (!matches.length) return null;
    return matches.find((variant) => variant.available) || matches[0];
  }

  function setAddButtonState(button, state, variantId) {
    const labelAdd = button.getAttribute('data-label-add') || 'Add to Bag';
    const labelSelect = button.getAttribute('data-label-select') || 'Select size';
    const labelSoldOut = button.getAttribute('data-label-sold-out') || 'Sold out';

    if (state === 'sold_out') {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = labelSoldOut;
      delete button.dataset.variantId;
      return;
    }

    if (state === 'ready' && variantId) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.textContent = labelAdd;
      button.dataset.variantId = String(variantId);
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.textContent = labelSelect;
    delete button.dataset.variantId;
  }

  function isPreorderAvailabilityRow(row) {
    return row?.dataset?.preorderAvailability === 'true';
  }

  function setActionState(row, state, variant) {
    const addButton = row.querySelector('[data-blog-look-add]');
    const notifyButton = row.querySelector('[data-blog-look-notify]');
    if (!addButton) return;

    if (state === 'notify' && variant && isPreorderAvailabilityRow(row)) {
      state = 'ready';
    }

    if (state === 'notify' && variant && notifyButton && !isPreorderAvailabilityRow(row)) {
      addButton.hidden = true;
      notifyButton.hidden = false;
      notifyButton.dataset.variantId = String(variant.id);
      return;
    }

    if (notifyButton) {
      notifyButton.hidden = true;
      delete notifyButton.dataset.variantId;
    }

    addButton.hidden = false;

    if (state === 'ready' && variant) {
      setAddButtonState(addButton, 'ready', variant.id);
      return;
    }

    if (state === 'sold_out') {
      setAddButtonState(addButton, 'sold_out');
      return;
    }

    setAddButtonState(addButton, 'select');
  }

  async function addVariantToCart(variantId, triggerEl) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    const sections = ['page-header', 'cart-drawer']
      .map((sel) => document.querySelector(sel))
      .filter(Boolean);
    const sectionIds = sections.map((el) => el?.dataset?.sectionId).filter(Boolean);
    if (sectionIds.length) {
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sectionIds.join(','));
    }

    const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    const data = await res.json();
    const errorMessage = data.description || data.message
      || (data.errors && typeof data.errors === 'object'
        ? Object.values(data.errors).flat().join(', ')
        : null);

    if (!res.ok || data.status) {
      throw new Error(errorMessage || 'Could not add to cart');
    }

    if (data.sections && sections.length) {
      sections.forEach((el) => {
        if (el?.dataset?.sectionId && data.sections[el.dataset.sectionId]) {
          el.updateFromCartChange?.(data.sections[el.dataset.sectionId]);
        }
      });
    } else {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    }

    window.theme?.setCartLastAddedProductId?.(data.product_id);
    document.dispatchEvent(new CustomEvent('on:cart:add', {
      bubbles: true,
      detail: { variantId: data.variant_id, productId: data.product_id }
    }));

    if (window.theme?.settings?.afterAddToCart === 'page') {
      window.location.href = window.theme.routes?.cart || '/cart';
      return data;
    }

    if (window.theme?.settings?.afterAddToCart === 'drawer') {
      document.querySelector('.js-cart-drawer')?.open?.(triggerEl);
    } else if (window.theme?.settings?.afterAddToCart === 'notification') {
      const template = document.getElementById('AddedNotification')?.content?.firstElementChild?.cloneNode(true);
      if (template) {
        template.dataset.productTitle = data.product_title || data.title || '';
        (document.querySelector('.pageheader--sticky') || document.body).appendChild(template);
      }
    }

    return data;
  }

  function initRow(row) {
    if (row.dataset.blogLookInit === 'true') return;
    row.dataset.blogLookInit = 'true';

    const requiresSize = row.getAttribute('data-requires-size') === 'true';
    const sizeOptionIndex = parseInt(row.getAttribute('data-size-option-index'), 10);
    const addButton = row.querySelector('[data-blog-look-add]');
    const errorEl = row.querySelector('[data-blog-look-error]');
    const sizeSelect = row.querySelector('[data-blog-look-size-select]');
    const variants = parseVariants(row);
    const hiddenVariant = row.querySelector('[data-blog-look-variant-id]');

    if (!addButton) return;

    if (!requiresSize && hiddenVariant) {
      setActionState(row, 'ready', { id: hiddenVariant.value, available: true });
    } else if (!requiresSize && variants.length === 1 && (variants[0].available || isPreorderAvailabilityRow(row))) {
      setActionState(row, 'ready', variants[0]);
    } else if (!requiresSize && variants.length === 1 && !variants[0].available) {
      setActionState(row, 'sold_out');
    } else if (requiresSize) {
      setActionState(row, 'select');
    }

    if (sizeSelect) {
      sizeSelect.addEventListener('change', () => {
        const selectedSize = sizeSelect.value;
        if (!selectedSize) {
          if (errorEl) errorEl.hidden = true;
          setActionState(row, 'select');
          return;
        }

        const variant = findVariantForSize(variants, sizeOptionIndex, selectedSize);
        if (!variant) {
          setActionState(row, 'select');
          if (errorEl) {
            errorEl.textContent = addButton.getAttribute('data-label-sold-out') || 'Sold out';
            errorEl.hidden = false;
          }
          return;
        }

        if (errorEl) errorEl.hidden = true;

        if (variant.available || isPreorderAvailabilityRow(row)) {
          setActionState(row, 'ready', variant);
        } else {
          setActionState(row, 'notify', variant);
        }
      });
    }

    addButton.addEventListener('click', async () => {
      const variantId = addButton.dataset.variantId;
      if (!variantId || addButton.disabled) return;

      const originalText = addButton.textContent;
      addButton.disabled = true;
      if (errorEl) errorEl.hidden = true;

      try {
        await addVariantToCart(variantId, addButton);
        addButton.textContent = (window.theme && window.theme.strings && window.theme.strings.addedToCartShort) || 'Added';
        window.setTimeout(() => {
          setActionState(row, 'ready', { id: variantId, available: true });
        }, 1600);
      } catch (err) {
        addButton.disabled = false;
        addButton.textContent = originalText;
        if (errorEl) {
          errorEl.textContent = err.message || 'Could not add to cart';
          errorEl.hidden = false;
        }
        document.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: err.message }
        }));
      }
    });
  }

  function init(root) {
    root.querySelectorAll('[data-blog-look-product]').forEach(initRow);
    window.initCartDrawerVariantDropdowns?.(root);
  }

  document.querySelectorAll('[data-blog-look-cart]').forEach(init);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-blog-look-cart]');
    if (root) init(root);
  });
})();
